import type { Health, StageName } from '../api/types';
import { LineChart, GroupedBarChart } from '../charts/charts';
import { cssVar, ordinal, series } from '../charts/chart';
import { Async, ChartOrTable, Grid, PanelCard } from '../components/Panel';
import { AnswerCell, Table } from '../components/Table';
import { TileRow, type TileSpec } from '../components/Tiles';
import { useApi } from '../hooks/useApi';
import { ms, num, pct, plural, shortDate } from '../lib/format';
import { useApp } from '../state/app';

const STAGES: StageName[] = ['intent_ms', 'retrieval_ms', 'generation_ms', 'total_ms'];
const STAGE_NAMES: Record<StageName, string> = {
  intent_ms: 'Intent',
  retrieval_ms: 'Retrieval',
  generation_ms: 'Generation',
  total_ms: 'Total',
};

/**
 * Percentiles, not averages, because a mean latency hides the slow tail users notice. This
 * panel is advanced-only, so the labels are the ones whoever runs the service already uses.
 */
function tilesFor(data: Health): TileSpec[] {
  const total = data.stages.total_ms;
  const turns = data.failures?.turns ?? 0;
  const failed = data.failures?.failed ?? 0;

  return [
    {
      label: 'Typical answer',
      value: ms(total?.p50),
      hint: `p50 over ${plural(total?.n ?? 0, 'timed turn')}`,
    },
    { label: 'Slowest few', value: ms(total?.p95), hint: 'p95 — one in twenty is slower than this' },
    {
      label: 'Failed turns',
      value: num(failed),
      hint: `of ${plural(turns, 'turn')} — asked, and got nothing back`,
    },
    {
      label: 'Failure rate',
      value: pct(turns ? failed / turns : null, 1),
      hint: 'a technical fault, not a missing document',
    },
  ];
}

export function HealthPanel() {
  const { days, system } = useApp();
  const query = useApi<Health>('/api/health-metrics', { days, system });

  return (
    <Async query={query} shape="page">
      {(data) => {
        const ramp = ordinal();
        const rows = STAGES.map((s) => ({ stage: STAGE_NAMES[s], ...data.stages[s] }));

        return (
          <div className="space-y-5">
            <TileRow tiles={tilesFor(data)} />

            <Grid>
              <ChartOrTable
                title="Latency by stage"
                hint="Percentiles, not averages — the tail is what users notice."
                chart={
                  <GroupedBarChart
                    label="Latency percentiles by pipeline stage"
                    format={ms}
                    groups={STAGES.map((s) => STAGE_NAMES[s])}
                    series={[
                      { name: 'p50', color: ramp[3], values: STAGES.map((s) => data.stages[s].p50) },
                      { name: 'p95', color: ramp[2], values: STAGES.map((s) => data.stages[s].p95) },
                      { name: 'p99', color: ramp[1], values: STAGES.map((s) => data.stages[s].p99) },
                    ]}
                  />
                }
                table={
                  <Table
                    rows={rows}
                    columns={[
                      { key: 'stage', label: 'Stage' },
                      { key: 'n', label: 'n', num: true },
                      { id: 'p50', label: 'p50', num: true, render: (r) => ms(r.p50), sortValue: (r) => r.p50 },
                      { id: 'p95', label: 'p95', num: true, render: (r) => ms(r.p95), sortValue: (r) => r.p95 },
                      { id: 'p99', label: 'p99', num: true, render: (r) => ms(r.p99), sortValue: (r) => r.p99 },
                      { id: 'max', label: 'max', num: true, render: (r) => ms(r.max), sortValue: (r) => r.max },
                    ]}
                  />
                }
              />

              <PanelCard title="Daily volume" hint="Turns per day, with failures marked.">
                <LineChart
                  label="Questions and failures per day"
                  labels={data.daily.map((d) => shortDate(d.day))}
                  series={[
                    {
                      name: 'Questions',
                      color: series()[0],
                      values: data.daily.map((d) => d.turns),
                    },
                    {
                      name: 'Failed',
                      color: cssVar('--chart-critical'),
                      values: data.daily.map((d) => d.failed),
                    },
                  ]}
                />
              </PanelCard>
            </Grid>

            <PanelCard title="Recent failures" hint="Turns where the user asked and got nothing.">
              <p className="mb-3 text-sm text-[var(--text-secondary)]">
                {num(data.failures?.failed || 0)} of {num(data.failures?.turns || 0)} turns failed in
                this window.
              </p>
              <Table
                rows={data.recent_failures}
                empty="No failed turns recorded."
                // Ten of the twenty the endpoint returns, so a bad window does not turn this
                // into a section to scroll past. The pager says how many are behind them.
                pageSize={10}
                columns={[
                  { id: 'ts', label: 'When', render: (r) => shortDate(r.ts), sortValue: (r) => r.ts },
                  { key: 'failure', label: 'Error' },
                  { id: 'query', label: 'Question', render: (r) => r.query || '— (not logged)' },
                  {
                    // Empty for a turn that failed before producing anything, truncated for
                    // one that died mid-sentence.
                    id: 'answer',
                    label: 'Sent before it broke',
                    render: (r) => <AnswerCell text={r.answer} />,
                  },
                  { key: 'user_role', label: 'Role' },
                ]}
              />
            </PanelCard>
          </div>
        );
      }}
    </Async>
  );
}
